import Pusher from 'pusher-js';
import { API_CONFIG } from '../config/api';

class PusherService {
  constructor() {
    this.pusher = null;
    this.userChannel = null;
    this.adminChannel = null;
    this.isConnected = false;
    this.currentUserId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * Initialize Pusher connection
   */
  initialize(userId, userRole = null) {
    // Don't reinitialize if already connected for the same user
    if (this.isConnected && this.currentUserId === userId && this.pusher) {
      console.log('[PusherService] Already connected for user:', userId);
      return this.pusher;
    }

    // Disconnect existing connection if user changed
    if (this.pusher && this.currentUserId !== userId) {
      console.log('[PusherService] User changed, disconnecting previous connection');
      this.disconnect();
    }

    const pusherConfig = API_CONFIG.PUSHER;
    const pusherKey = pusherConfig.key;
    const pusherCluster = pusherConfig.cluster;

    if (!pusherConfig.enabled || !pusherKey || pusherKey === 'your-pusher-key' || pusherKey.trim() === '') {
      console.warn('[PusherService] ❌ Pusher not configured');
      return null;
    }

    console.log('[PusherService] Initializing Pusher connection...', {
      userId,
      key: pusherKey ? `${pusherKey.substring(0, 5)}...` : 'undefined',
      cluster: pusherCluster,
    });

    // Create Pusher instance
    this.pusher = new Pusher(pusherKey, {
      cluster: pusherCluster,
      encrypted: true,
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
      disabledTransports: [],
    });

    // Set up connection event listeners
    this.setupConnectionListeners();

    // Subscribe to user channel
    this.subscribeToUserChannel(userId);

    // Subscribe to admin channel if user is admin
    if (userRole === 'admin' || userRole === 'super_admin') {
      this.subscribeToAdminChannel(userId);
    }

    this.currentUserId = userId;
    return this.pusher;
  }

  /**
   * Set up connection event listeners
   */
  setupConnectionListeners() {
    if (!this.pusher) return;

    // Connection successful
    this.pusher.connection.bind('connected', () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] ✅ Pusher connected successfully');
      }
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Notify backend that user is online when Pusher connects (debounced)
      // Only update if not already online to prevent unnecessary broadcasts
      if (this.currentUserId) {
        // Small delay to debounce rapid reconnections
        setTimeout(() => {
          this.notifyUserOnline();
        }, 1000);
      }
      
      // Dispatch custom event for components to listen
      window.dispatchEvent(new CustomEvent('pusher:connected'));
    });

    // Connection disconnected
    this.pusher.connection.bind('disconnected', () => {
      console.log('[PusherService] ⚠️ Pusher disconnected');
      this.isConnected = false;
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('pusher:disconnected'));
      
      // Notify backend that user went offline (if we have a user ID)
      if (this.currentUserId) {
        this.notifyUserOffline();
      }
      
      // Attempt to reconnect
      this.attemptReconnect();
    });

    // Connection error
    this.pusher.connection.bind('error', (err) => {
      console.error('[PusherService] ❌ Pusher connection error:', err);
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('pusher:error', { detail: err }));
    });

    // State change
    this.pusher.connection.bind('state_change', (states) => {
      console.log('[PusherService] Pusher state changed:', states.previous, '->', states.current);
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('pusher:state_change', { detail: states }));
    });
  }

  /**
   * Subscribe to user-specific notification channel
   */
  subscribeToUserChannel(userId) {
    if (!this.pusher || !userId) return;

    const channelName = `user-${userId}-notifications`;
    console.log('[PusherService] Subscribing to user channel:', channelName);

    // Unsubscribe from previous channel if exists
    if (this.userChannel) {
      this.pusher.unsubscribe(this.userChannel.name);
    }

    this.userChannel = this.pusher.subscribe(channelName);

    // Subscription succeeded
    this.userChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[PusherService] ✅ Successfully subscribed to user channel:', channelName);
      console.log('[PusherService] Channel state:', this.userChannel.subscribed);
      console.log('[PusherService] Pusher connection state:', this.pusher.connection.state);
      window.dispatchEvent(new CustomEvent('pusher:user-channel-subscribed', { detail: { channelName } }));
    });

    // Subscription error
    this.userChannel.bind('pusher:subscription_error', (error) => {
      console.error('[PusherService] ❌ User channel subscription error:', error);
      window.dispatchEvent(new CustomEvent('pusher:user-channel-error', { detail: error }));
    });

    // Bind to announcement events
    this.userChannel.bind('announcement', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] 📢 Announcement received on channel:', channelName, data);
      }
      const customEvent = new CustomEvent('pusher:announcement', { detail: data });
      window.dispatchEvent(customEvent);
    });

    // Bind to notification events
    this.userChannel.bind('notification', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] 🔔 Notification received:', data);
      }
      window.dispatchEvent(new CustomEvent('pusher:notification', { detail: data }));
    });

    // Bind to other existing events
    this.userChannel.bind('forum-status-updated', (data) => {
      window.dispatchEvent(new CustomEvent('pusher:forum-status-updated', { detail: data }));
    });

    this.userChannel.bind('admin-reply', (data) => {
      window.dispatchEvent(new CustomEvent('pusher:admin-reply', { detail: data }));
    });

    this.userChannel.bind('job-compliance-violation', (data) => {
      window.dispatchEvent(new CustomEvent('pusher:job-compliance', { detail: data }));
    });

    this.userChannel.bind('job-application', (data) => {
      console.log('[PusherService] 📬 Job application notification received:', data);
      window.dispatchEvent(new CustomEvent('pusher:job-application', { detail: data }));
    });

    this.userChannel.bind('job-application-status', (data) => {
      console.log('[PusherService] 📬 Job application status notification received:', data);
      window.dispatchEvent(new CustomEvent('pusher:job-application-status', { detail: data }));
    });

    // Bind to chat events
    this.userChannel.bind('new-message', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] 💬 New message received:', data);
      }
      window.dispatchEvent(new CustomEvent('pusher:new-message', { detail: data }));
    });

    this.userChannel.bind('chat-room-created', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] 🏠 Chat room created:', data);
      }
      window.dispatchEvent(new CustomEvent('pusher:chat-room-created', { detail: data }));
    });

    this.userChannel.bind('messages-read', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] ✓ Messages read:', data);
      }
      window.dispatchEvent(new CustomEvent('pusher:messages-read', { detail: data }));
    });

    this.userChannel.bind('chat-unread-count-updated', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] 📊 Chat unread count updated:', data);
      }
      window.dispatchEvent(new CustomEvent('pusher:chat-unread-count-updated', { detail: data }));
    });

    // Bind to user online/offline events
    this.userChannel.bind('user-online', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] 🟢 User online:', data);
      }
      window.dispatchEvent(new CustomEvent('pusher:user-online', { detail: data }));
    });

    this.userChannel.bind('user-offline', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PusherService] 🔴 User offline:', data);
      }
      window.dispatchEvent(new CustomEvent('pusher:user-offline', { detail: data }));
    });
  }

  /**
   * Subscribe to admin notification channel
   */
  subscribeToAdminChannel(adminId) {
    if (!this.pusher || !adminId) return;

    const channelName = `admin-${adminId}-notifications`;
    console.log('[PusherService] Subscribing to admin channel:', channelName);

    // Unsubscribe from previous channel if exists
    if (this.adminChannel) {
      this.pusher.unsubscribe(this.adminChannel.name);
    }

    this.adminChannel = this.pusher.subscribe(channelName);

    // Subscription succeeded
    this.adminChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[PusherService] ✅ Successfully subscribed to admin channel:', channelName);
      window.dispatchEvent(new CustomEvent('pusher:admin-channel-subscribed', { detail: { channelName } }));
    });

    // Subscription error
    this.adminChannel.bind('pusher:subscription_error', (error) => {
      console.error('[PusherService] ❌ Admin channel subscription error:', error);
      window.dispatchEvent(new CustomEvent('pusher:admin-channel-error', { detail: error }));
    });

    // Bind to admin notification events
    this.adminChannel.bind('admin-notification', (data) => {
      console.log('[PusherService] 🔔 Admin notification received:', data);
      window.dispatchEvent(new CustomEvent('pusher:admin-notification', { detail: data }));
    });

    this.adminChannel.bind('job-report', (data) => {
      window.dispatchEvent(new CustomEvent('pusher:job-report', { detail: data }));
    });
  }

  /**
   * Attempt to reconnect if disconnected
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[PusherService] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // Exponential backoff, max 30s

    console.log(`[PusherService] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`);

    setTimeout(() => {
      if (this.pusher && this.currentUserId) {
        // Try to reconnect
        if (this.pusher.connection.state === 'disconnected' || this.pusher.connection.state === 'failed') {
          console.log('[PusherService] Reconnecting...');
          this.pusher.connect();
        }
      }
    }, delay);
  }

  /**
   * Disconnect Pusher
   */
  async disconnect() {
    // Notify backend that user is going offline before disconnecting
    if (this.currentUserId && this.isConnected) {
      await this.notifyUserOffline();
    }

    if (this.userChannel) {
      this.pusher?.unsubscribe(this.userChannel.name);
      this.userChannel = null;
    }

    if (this.adminChannel) {
      this.pusher?.unsubscribe(this.adminChannel.name);
      this.adminChannel = null;
    }

    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = null;
    }

    this.isConnected = false;
    this.currentUserId = null;
    this.reconnectAttempts = 0;
    console.log('[PusherService] Disconnected');
  }

  /**
   * Get Pusher instance
   */
  getPusher() {
    return this.pusher;
  }

  /**
   * Get user channel
   */
  getUserChannel() {
    return this.userChannel;
  }

  /**
   * Get admin channel
   */
  getAdminChannel() {
    return this.adminChannel;
  }

  /**
   * Check if connected
   */
  getIsConnected() {
    return this.isConnected && this.pusher?.connection.state === 'connected';
  }

  /**
   * Notify backend that user is online
   */
  async notifyUserOnline() {
    if (!this.currentUserId) {
      return;
    }

    try {
      // Import API config to get the base URL
      const { API_CONFIG } = await import('../config/api');
      const apiUrl = `${API_CONFIG.BASE_URL}/chat/user/status`;
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        return;
      }

      // Update status to online
      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ is_online: true }),
      }).catch((error) => {
        console.error('[PusherService] Error notifying online status:', error);
      });
    } catch (error) {
      console.error('[PusherService] Error notifying online status:', error);
    }
  }

  /**
   * Notify backend that user went offline
   * This is called when Pusher disconnects (browser close, network issues, etc.)
   */
  async notifyUserOffline() {
    if (!this.currentUserId) {
      return;
    }

    try {
      // Import API config to get the base URL
      const { API_CONFIG } = await import('../config/api');
      const apiUrl = `${API_CONFIG.BASE_URL}/chat/user/status`;
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        return;
      }

      // Use fetch with keepalive flag for reliability during page unload
      // This allows the request to continue even after the page is closed
      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ is_online: false }),
        keepalive: true, // Critical for page unload scenarios
      }).catch(() => {
        // Silently fail - this is expected during page unload
        // The request may not complete, but we tried
      });
    } catch (error) {
      // Silently fail during page unload
      console.error('[PusherService] Error notifying offline status:', error);
    }
  }
}

// Export singleton instance
export const pusherService = new PusherService();
export default pusherService;

